import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import Navbar from "./pages/navbar";
import Home from "./pages/Home";
import About from "./pages/About";
import Menu from "./pages/Menu";
import Visit from "./pages/Visit";
import SignUp from "./pages/Signup";
import Login from "./pages/Login";
import Footer from "./pages/footer";
import Order from "./pages/Order";
import Blog from "./pages/blog";
import BlogPost from "./pages/BlogPost";
import Cart from "./pages/Cart";
import ProductDetail from "./pages/productdetail";
import Contact from "./pages/contact";
import Checkout from "./pages/checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ShippingPolicyPage from "./pages/ShippingPolicyPage";
import ReturnPolicyPage from "./pages/ReturnPolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";
import Profile from "./pages/Profile";
import Reviews from "./pages/Reviews";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOrders from "./pages/AdminOrders";
import AdminBlog from "./pages/AdminBlog";
import AdminProducts from "./pages/AdminProducts";
import AdminReviews from "./pages/AdminReviews";
import AdminPromotions from "./pages/AdminPromotions";
import Products from "./pages/Products";
import SEO, { SITE_NAME, SITE_URL } from "./components/SEO";
import { CartProvider } from "./context/cartcontext";
import { AuthProvider } from "./context/AuthContext";
import heroImage from "./assets/zeamays.png";
import logoImage from "./assets/zeamays.png";

const useCriticalAssetPreloads = () => {
  useEffect(() => {
    const criticalImages = [logoImage, heroImage].filter(Boolean);

    criticalImages.forEach((src) => {
      if (document.querySelector(`link[rel="preload"][as="image"][href="${src}"]`)) {
        return;
      }

      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = src;
      document.head.appendChild(link);
    });
  }, []);
};

const localBusinessStructuredData = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: SITE_NAME,
  url: SITE_URL,
  image: `${SITE_URL}/zeamays-favicon.png`,
  telephone: "+2347037038448",
  email: "zeamaysfood@gmail.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "No. 2, Ojo Akinbinu Street, Elepe",
    addressLocality: "Ikorodu",
    addressRegion: "Lagos",
    addressCountry: "NG",
  },
  areaServed: "Ikorodu, Lagos, Nigeria",
};

const getRouteMetadata = (pathname) => {
  if (pathname.startsWith("/product/") || pathname.startsWith("/blog/")) {
    return null;
  }

  const metadata = {
    "/": {
      title: "Nigerian Food Products Online in Ikorodu | Zea Mays Foods",
      description: "Shop quality Nigerian pantry staples, flours, pap, spices, and soup ingredients from Zea Mays Foods in Ikorodu, Lagos.",
      structuredData: localBusinessStructuredData,
    },
    "/about": {
      title: "About Zea Mays Foods | Nigerian Food Processing in Ikorodu",
      description: "Learn about Zea Mays Foods, a Nigerian food processing and wellness company serving quality local food products from Ikorodu, Lagos.",
    },
    "/products": {
      title: "Shop Nigerian Food Products Online | Zea Mays Foods",
      description: "Browse and order ogbono, crayfish, pap, yam flour, poundo, locust beans, and other Nigerian food products from Zea Mays Foods.",
    },
    "/menu": {
      title: "Zea Mays Foods Menu | Nigerian Pantry Products",
      description: "Explore the Zea Mays Foods menu of Nigerian pantry staples, soup ingredients, flours, pap, and everyday food products.",
    },
    "/visit": {
      title: "Visit Zea Mays Foods in Ikorodu, Lagos",
      description: "Find Zea Mays Foods at No. 2, Ojo Akinbinu Street, Elepe, Ikorodu, Lagos, or contact us about pickup and product availability.",
      structuredData: localBusinessStructuredData,
    },
    "/contact": {
      title: "Contact Zea Mays Foods | Ikorodu, Lagos",
      description: "Contact Zea Mays Foods in Ikorodu, Lagos for product questions, delivery support, orders, and general enquiries.",
      structuredData: localBusinessStructuredData,
    },
    "/blog": {
      title: "Zea Mays Foods Blog | Nigerian Food Tips and Stories",
      description: "Read Nigerian food tips, recipes, product updates, and kitchen stories from Zea Mays Foods.",
    },
    "/reviews": {
      title: "Customer Reviews | Zea Mays Foods",
      description: "Read customer reviews and experiences with Zea Mays Foods products and service.",
    },
    "/privacy-policy": {
      title: "Privacy Policy | Zea Mays Foods",
      description: "Read the Zea Mays Foods Privacy Policy and learn how we handle customer information and website data.",
    },
    "/shipping-policy": {
      title: "Shipping Policy | Zea Mays Foods",
      description: "Learn about Zea Mays Foods delivery areas, shipping charges, order processing, and delivery conditions.",
    },
    "/return-policy": {
      title: "Return Policy | Zea Mays Foods",
      description: "Read the Zea Mays Foods return, replacement, and refund policy for food product orders.",
    },
    "/terms-and-conditions": {
      title: "Terms and Conditions | Zea Mays Foods",
      description: "Review the terms and conditions for using the Zea Mays Foods website and placing orders online.",
    },
  };

  if (metadata[pathname]) {
    return { ...metadata[pathname], path: pathname };
  }

  return {
    title: "Zea Mays Foods | Nigerian Food Products",
    description: "Shop quality Nigerian food products, pantry staples, and food ingredients from Zea Mays Foods.",
    path: pathname,
    noindex: true,
  };
};

const RouteSEO = () => {
  const { pathname } = useLocation();
  const metadata = getRouteMetadata(pathname);

  return metadata ? <SEO {...metadata} /> : null;
};

const AppShell = () => {
  useCriticalAssetPreloads();

  return (
    <>
      <Navbar />
      <RouteSEO />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/menu" element={<Menu />} />
        <Route path="/visit" element={<Visit />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/order" element={<Order />} />
        <Route path="/orders" element={<Order />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout-success" element={<CheckoutSuccess />} />
        <Route path="/checkout-cancel" element={<CheckoutCancel />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
        <Route path="/return-policy" element={<ReturnPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
        <Route path="/terms" element={<TermsConditionsPage />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/blog" element={<AdminBlog />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/reviews" element={<AdminReviews />} />
        <Route path="/admin/promotions" element={<AdminPromotions />} />
        <Route path="/products" element={<Products />} />
      </Routes>
      <Footer />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <AppShell />
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
